import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/**
 * Thin client cart. Holds only SKU references + quantities in localStorage —
 * the server is the source of truth for prices and recomputes totals at
 * checkout, so a tampered cart can't change what you pay.
 */
const CART_KEY = "piggy.shop.cart";

export interface CartLine {
  sku_code: string;
  quantity: number;
  // Display-only snapshot (server re-prices at checkout).
  name: string;
  price_cents: number;
  currency: string;
}

interface CartValue {
  lines: CartLine[];
  count: number;
  add: (line: Omit<CartLine, "quantity">, qty?: number) => void;
  remove: (sku_code: string) => void;
  setQty: (sku_code: string, qty: number) => void;
  clear: () => void;
}

const Ctx = createContext<CartValue | null>(null);

export function useCart(): CartValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useCart used outside CartProvider");
  return v;
}

function read(): CartLine[] {
  try {
    const raw = localStorage.getItem(CART_KEY);
    return raw ? (JSON.parse(raw) as CartLine[]) : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [lines, setLines] = useState<CartLine[]>(() => read());

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(lines));
  }, [lines]);

  const add = useCallback((line: Omit<CartLine, "quantity">, qty = 1) => {
    setLines((prev) => {
      const i = prev.findIndex((l) => l.sku_code === line.sku_code);
      if (i >= 0) {
        const next = [...prev];
        next[i] = { ...next[i], quantity: next[i].quantity + qty };
        return next;
      }
      return [...prev, { ...line, quantity: qty }];
    });
  }, []);

  const remove = useCallback((sku_code: string) => {
    setLines((prev) => prev.filter((l) => l.sku_code !== sku_code));
  }, []);

  const setQty = useCallback((sku_code: string, qty: number) => {
    setLines((prev) =>
      prev
        .map((l) => (l.sku_code === sku_code ? { ...l, quantity: Math.max(1, qty) } : l))
        .filter((l) => l.quantity > 0),
    );
  }, []);

  const clear = useCallback(() => setLines([]), []);

  const value = useMemo<CartValue>(
    () => ({
      lines,
      count: lines.reduce((n, l) => n + l.quantity, 0),
      add,
      remove,
      setQty,
      clear,
    }),
    [lines, add, remove, setQty, clear],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
